import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { WHATSAPP_NUMBER_DISPLAY } from "@/lib/config";

// ⚠️ TEXTOS LEGALES DE EJEMPLO (genéricos).
// Sustituye los datos entre [corchetes] por los datos reales de tu empresa
// y, si puedes, pídele a un profesional que los revise.

const PAGES = {
    "aviso-legal": {
        title: "Aviso legal",
        sections: [
            {
                h: "1. Titular del sitio web",
                p: `En cumplimiento de la Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se informa de que el titular de este sitio web es Puertalia Portal [razón social completa pendiente], con NIF [pendiente] y domicilio social en [dirección completa pendiente]. Contacto: WhatsApp ${WHATSAPP_NUMBER_DISPLAY}.`,
            },
            {
                h: "2. Objeto",
                p: "Este sitio web tiene carácter exclusivamente informativo. Su única finalidad es dar a conocer la aplicación móvil de Puertalia Portal, un portal inmobiliario de pisos y casas en alquiler y venta en toda España. En esta web no se publican inmuebles, no se realizan reservas ni se gestionan pagos de ningún tipo.",
            },
            {
                h: "3. Condiciones de uso",
                p: "El acceso y uso de este sitio web es gratuito. El usuario se compromete a hacer un uso lícito de los contenidos. Puertalia Portal no se responsabiliza del uso indebido que terceros puedan hacer de la información publicada.",
            },
            {
                h: "4. Propiedad intelectual",
                p: "Todos los contenidos de este sitio (marca, logotipo, textos, diseño e imágenes) son titularidad de Puertalia Portal o se usan con licencia, y no pueden reproducirse sin autorización expresa.",
            },
            {
                h: "5. Contacto y operativa",
                p: "Toda la comunicación con Puertalia Portal se realiza a través de WhatsApp. No pedimos dinero ni documentación por adelantado: cualquier gestión relacionada con un inmueble se formaliza siempre en persona. Desconfía de cualquiera que actúe en nuestro nombre pidiéndote pagos por adelantado.",
            },
        ],
    },
    privacidad: {
        title: "Política de privacidad",
        sections: [
            {
                h: "1. Responsable del tratamiento",
                p: `Responsable: Puertalia Portal [razón social completa pendiente], con NIF [pendiente] y domicilio en [dirección completa pendiente]. Contacto: WhatsApp ${WHATSAPP_NUMBER_DISPLAY}.`,
            },
            {
                h: "2. Datos que recogemos",
                p: "Esta web no contiene formularios ni registros, por lo que no recogemos datos personales a través de ella. Si nos escribes por WhatsApp, trataremos tu número y el contenido de la conversación únicamente para atender tu consulta sobre inmuebles, conforme al Reglamento (UE) 2016/679 (RGPD) y la LOPDGDD 3/2018.",
            },
            {
                h: "3. Finalidad y conservación",
                p: "Los datos facilitados por WhatsApp se usan solo para responder a tu solicitud y gestionar, en su caso, las visitas a inmuebles. Se conservan el tiempo necesario para atender la consulta y nunca se ceden a terceros ni se usan con fines publicitarios sin tu consentimiento.",
            },
            {
                h: "4. Tus derechos",
                p: "Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiéndonos por WhatsApp. También puedes reclamar ante la Agencia Española de Protección de Datos (www.aepd.es).",
            },
        ],
    },
    cookies: {
        title: "Política de cookies",
        sections: [
            {
                h: "1. ¿Qué son las cookies?",
                p: "Las cookies son pequeños archivos que se guardan en tu dispositivo cuando visitas una web. Sirven para que el sitio funcione correctamente y para conocer de forma anónima cómo se usa.",
            },
            {
                h: "2. Cookies que usamos",
                p: "Cookies técnicas (necesarias): recuerdan tu elección sobre el aviso de cookies. Cookies de análisis (opcionales): nos ayudan a entender las visitas de forma agregada y anónima para mejorar la web. No usamos cookies publicitarias ni de seguimiento individual.",
            },
            {
                h: "3. Cómo aceptar o rechazar",
                p: "Al entrar por primera vez verás un aviso donde puedes aceptar o rechazar las cookies de análisis. Para cambiar tu decisión, borra los datos de este sitio en la configuración de tu navegador y vuelve a entrar: el aviso aparecerá de nuevo.",
            },
            {
                h: "4. Desactivar cookies en el navegador",
                p: "Puedes configurar tu navegador (Chrome, Firefox, Safari, Edge) para bloquear o eliminar las cookies. Ten en cuenta que bloquear las cookies técnicas puede afectar al funcionamiento de la web.",
            },
        ],
    },
};

const Legal = ({ page }) => {
    const content = PAGES[page] || PAGES["aviso-legal"];

    return (
        <div className="min-h-screen bg-brand-cream dark:bg-brand-night" data-testid={`legal-page-${page}`}>
            <header className="border-b border-white/10 bg-brand-ink">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
                    <Link to="/" aria-label="Volver al inicio">
                        <Logo light />
                    </Link>
                    <Link
                        to="/"
                        data-testid="legal-back-btn"
                        className="flex items-center gap-2 font-sans text-sm font-bold text-white/70 transition-colors duration-200 hover:text-white"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Volver
                    </Link>
                </div>
            </header>
            <main className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
                <p className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-4 font-sans text-xs text-brand-navy/70 dark:text-white/60">
                    Texto legal de ejemplo: sustituye los datos entre [corchetes] por los
                    datos reales de tu empresa antes de publicar la web.
                </p>
                <h1 className="mt-8 font-sans text-3xl font-extrabold tracking-tight text-brand-navy dark:text-white sm:text-4xl">
                    {content.title}
                </h1>
                <div className="mt-10 space-y-9">
                    {content.sections.map((section) => (
                        <section key={section.h}>
                            <h2 className="font-sans text-lg font-bold text-brand-navy dark:text-white">
                                {section.h}
                            </h2>
                            <p className="mt-2.5 text-sm leading-relaxed text-brand-navy/75 dark:text-white/65 sm:text-base">
                                {section.p}
                            </p>
                        </section>
                    ))}
                </div>
                <p className="mt-14 border-t border-brand-navy/10 pt-6 font-sans text-xs text-brand-navy/50 dark:border-white/10 dark:text-white/40">
                    © 2026 Puertalia Portal · Última actualización: enero de 2026
                </p>
            </main>
        </div>
    );
};

export default Legal;
